import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, resolvePhotoUrl } from "@/lib/api";
import { MobileShell } from "@/components/MobileShell";
import { ChevronLeft, Minus, Plus, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSettings, effectivePrice } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export default function ProductDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [qty, setQty] = useState(1);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const { addItem } = useCart();
    const { currency_symbol } = useSettings();

    useEffect(() => {
        api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(false));
    }, [id]);

    if (product === false) {
        return (
            <MobileShell>
                <p className="text-center py-16 text-muted-foreground">Product not found.</p>
                <div className="text-center">
                    <Link
                        to="/"
                        data-testid="back-to-shop"
                        className="text-primary underline underline-offset-4"
                    >
                        Back to shop
                    </Link>
                </div>
            </MobileShell>
        );
    }
    if (!product) {
        return (
            <MobileShell>
                <div className="animate-pulse space-y-4 mt-4">
                    <div className="aspect-square rounded-3xl bg-surface/60" />
                    <div className="h-6 bg-surface/60 rounded w-2/3" />
                </div>
            </MobileShell>
        );
    }

    const hasDisc = Number(product.discount_percent || 0) > 0;
    const eff = effectivePrice(product);

    const onAdd = () => {
        addItem({ ...product, price: eff }, qty);
        toast.success(`${qty} × ${product.name} added`);
        navigate("/checkout");
    };

    return (
        <MobileShell showCart={false}>
            <button
                data-testid="back-btn"
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 text-sm text-muted-foreground py-2"
            >
                <ChevronLeft className="h-4 w-4" />
                Back
            </button>

            <div className="md:grid md:grid-cols-2 md:gap-12 md:items-start md:mt-4">
                <div
                    className="aspect-square w-full rounded-3xl overflow-hidden bg-border/20 animate-fadeUp md:sticky md:top-24 cursor-zoom-in relative"
                    data-testid="product-hero"
                    onClick={() => product.photo_url && setLightboxOpen(true)}
                >
                    {product.photo_url && (
                        <img
                            src={resolvePhotoUrl(product.photo_url)}
                            alt={product.name}
                            className="w-full h-full object-cover"
                        />
                    )}
                    {hasDisc && (
                        <span className="absolute top-3 left-3 bg-primary text-primary-foreground text-[10px] font-semibold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full shadow-sm">
                            -{product.discount_percent}%
                        </span>
                    )}
                </div>

                {lightboxOpen && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
                        onClick={() => setLightboxOpen(false)}
                    >
                        <button
                            className="absolute top-4 right-4 text-white/80 hover:text-white"
                            onClick={() => setLightboxOpen(false)}
                            aria-label="Close"
                        >
                            <X className="h-8 w-8" />
                        </button>
                        <img
                            src={resolvePhotoUrl(product.photo_url)}
                            alt={product.name}
                            className="max-h-[90vh] max-w-[90vw] object-contain rounded-2xl"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                )}

                <div className="mt-6 md:mt-0 space-y-4 animate-fadeUp">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                        {product.category}
                    </p>
                    <h1
                        className="font-serif text-3xl md:text-5xl leading-tight"
                        data-testid="product-name"
                    >
                        {product.name}
                    </h1>                   
                    <div className="flex items-center justify-between pt-2">
                        <div className="flex items-baseline gap-3">
                            <span
                                data-testid="product-price"
                                className="font-serif text-3xl md:text-4xl text-primary font-semibold"
                            >
                                {currency_symbol}{eff.toFixed(2)}
                            </span>
                            {hasDisc && (
                                <span className="text-base line-through text-muted-foreground">
                                    {currency_symbol}{product.price.toFixed(2)}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 border border-border/60 rounded-full px-2 py-1">
                            <button
                                data-testid="qty-decrement"
                                className="p-2 hover:text-primary transition-colors"
                                onClick={() => setQty(Math.max(1, qty - 1))}
                            >
                                <Minus className="h-4 w-4" />
                            </button>
                            <span className="w-6 text-center font-medium" data-testid="qty-value">
                                {qty}
                            </span>
                            <button
                                data-testid="qty-increment"
                                className="p-2 hover:text-primary transition-colors"
                                onClick={() => setQty(qty + 1)}
                            >
                                <Plus className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                    <Button
                        data-testid="add-to-cart-detail"
                        onClick={onAdd}
                        className="w-full h-14 rounded-full bg-primary hover:bg-primary-hover text-primary-foreground text-base"
                    >
                        Add {qty} to bag — {currency_symbol}{(eff * qty).toFixed(2)}
                    </Button>
                </div>
            </div>
        </MobileShell>
    );
}
